import { useEffect, useState } from "react";
import { User, Mail, Shield, Warehouse, Bell, BellOff, LogOut, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { ROLE_LABEL } from "@/lib/constants";
import { pushSupported, isPushEnabled, enablePush, disablePush } from "@/lib/push";

export default function MobileProfilo() {
  const { user, logout } = useAuth();
  const [pushOn, setPushOn] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { isPushEnabled().then(setPushOn); }, []);

  const togglePush = async () => {
    setBusy(true);
    try {
      if (pushOn) { await disablePush(); setPushOn(false); toast.success("Notifiche push disattivate"); }
      else { await enablePush(); setPushOn(true); toast.success("Notifiche push attivate"); }
    } catch (e) { toast.error(e.message || "Errore notifiche push"); }
    finally { setBusy(false); }
  };

  const Row = ({ icon: Icon, label, value }) => (
    <div className="flex items-center gap-3 border-b border-border/60 py-3 last:border-0">
      <Icon size={18} className="shrink-0 text-muted-foreground" />
      <div className="min-w-0">
        <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</p>
        <p className="truncate text-sm font-semibold text-navy">{value || "—"}</p>
      </div>
    </div>
  );

  return (
    <div className="space-y-5 animate-fade-up">
      <div className="flex flex-col items-center pt-4 text-center">
        <span className="flex h-20 w-20 items-center justify-center rounded-full bg-navy text-2xl font-extrabold text-white">
          {user?.name?.[0]?.toUpperCase()}
        </span>
        <h1 className="mt-3 text-xl font-extrabold tracking-tight text-navy">{user?.name}</h1>
        <span className="mt-1 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-0.5 text-xs font-semibold text-emerald-700">{ROLE_LABEL[user?.role]}</span>
      </div>

      <div className="rounded-xl border border-border bg-card px-4">
        <Row icon={Mail} label="Email" value={user?.email} />
        <Row icon={Shield} label="Ruolo" value={ROLE_LABEL[user?.role]} />
        <Row icon={Warehouse} label="Magazzino mobile" value={user?.magazzino_warehouse_name} />
      </div>

      <div className="rounded-xl border border-border bg-card p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            {pushOn ? <Bell size={18} className="text-primary" /> : <BellOff size={18} className="text-muted-foreground" />}
            <div>
              <p className="text-sm font-bold text-navy">Notifiche push</p>
              <p className="text-xs text-muted-foreground">{pushOn ? "Attive su questo dispositivo" : "Ricevi avvisi sui nuovi ticket"}</p>
            </div>
          </div>
          <Button variant={pushOn ? "outline" : "default"} size="sm" onClick={togglePush} disabled={busy || !pushSupported()} data-testid="toggle-push">
            {busy ? <Loader2 size={15} className="animate-spin" /> : pushOn ? "Disattiva" : "Attiva"}
          </Button>
        </div>
        {!pushSupported() && <p className="mt-2 text-xs text-amber-700">Su iPhone aggiungi prima l'app alla schermata Home per abilitare le notifiche.</p>}
      </div>

      <Button variant="outline" className="w-full text-destructive" onClick={logout} data-testid="profilo-logout">
        <LogOut size={16} />Esci
      </Button>
    </div>
  );
}
